"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ViewPropertyFilter = void 0;
const datasheet_api_1 = require("../modules/database/api/datasheet_api");
const commands_1 = require("../commands");
const i18n_1 = require("../exports/i18n");
const model_1 = require("../model");
const store_1 = require("../exports/store");
const selectors_1 = require("../exports/store/selectors");
const types_1 = require("../types");
class ViewPropertyFilter {
    constructor(_getState, _dispatch, _datasheetId, _listener) {
        this._getState = _getState;
        this._dispatch = _dispatch;
        this._datasheetId = _datasheetId;
        this._listener = _listener;
    }
    _checkGroupInfo(groupInfo, fieldMap) {
        return groupInfo.every(info => {
            return fieldMap[info.fieldId];
        });
    }
    _checkFilterInfo(filterInfo, fieldMap) {
        if (!filterInfo.conditions.length) {
            return true;
        }
        return filterInfo.conditions.every(info => {
            return fieldMap[info.fieldId];
        });
    }
    _checkSortInfo(sortInfo, fieldMap) {
        if (!sortInfo.rules.length) {
            return true;
        }
        return sortInfo.rules.every(info => {
            return fieldMap[info.fieldId];
        });
    }
    _checkStyle(style, fieldMap) {
        if (style.kanbanFieldId) {
            return fieldMap[style.kanbanFieldId];
        }
        return true;
    }
    /* Strategy for checking data integrity */
    _fieldIntegrityCheck(viewProperty, op, fieldMap) {
        switch (viewProperty) {
            case 'groupInfo': {
                return this._checkGroupInfo(op, fieldMap);
            }
            case 'filterInfo': {
                return this._checkFilterInfo(op, fieldMap);
            }
            case 'sortInfo': {
                return this._checkSortInfo(op, fieldMap);
            }
            case 'style': {
                return this._checkStyle(op, fieldMap);
            }
            default: {
                return true;
            }
        }
    }
    _actionFilter(action) {
        const path = action.p;
        if (!path.includes('views')) {
            return true;
        }
        const state = this._getState();
        const viewIndex = path[2];
        const snapshot = store_1.Selectors.getSnapshot(state, this._datasheetId);
        if (!snapshot) {
            return false;
        }
        const views = snapshot.meta.views;
        const view = views[viewIndex];
        const opViewId = view === null || view === void 0 ? void 0 : view.id;
        if (!view || !opViewId) {
            return true;
        }
        if (view['autoSave']) {
            return true;
        }
        const propertyKey = path[3];
        if (!propertyKey) {
            // If there is no propertyKey, it should be doing the delete operation of the view, and the view deletion also needs to be released
            return true;
        }
        if (ViewPropertyFilter.ignoreViewProperty.includes(propertyKey)) {
            return true;
        }
        if (this._fromServer && path.includes('autoSave') && action['oi']) {
            // Receive op from the server, if it is checked that there is a modification to autoSave,
            // and it is turned on, you need to pull the latest view data from the server to overwrite the local
            // FIXME handle errors
            void ViewPropertyFilter.resetViewProperty(state, {
                datasheetId: this._datasheetId,
                viewId: view.id,
                dispatch: this._dispatch,
                onError: this._listener.onError
            });
            return true;
        }
        if (path.includes('columns')) {
            // If the li and ld operations are performed at the same time,
            // it means that the original content is being replaced, which belongs to LR and needs to be filtered out
            // Such as hiding columns, modifying column widths, etc.
            if (action['li'] && action['ld']) {
                return false;
            }
            // There is only one li and ld, just delete or add
            if (action['li'] || action['ld']) {
                return true;
            }
            return false;
        }
        return false;
    }
    parseActions(actions, { fromServer, commandName }) {
        var _a, _b, _c;
        this._fromServer = fromServer;
        if (commandName &&
            [commands_1.CollaCommandName.AddViews, commands_1.CollaCommandName.DeleteViews, commands_1.CollaCommandName.MoveViews, commands_1.CollaCommandName.DeleteField,
                commands_1.CollaCommandName.SetFieldAttr].includes(commandName)) {
            // If it is detected that the collaborative state of the view is being modified, there is no need to filter this action.
            return actions;
        }
        const state = this._getState();
        if (!((_a = state.labs) === null || _a === void 0 ? void 0 : _a.includes('view_manual_save')) && !((_b = state.share) === null || _b === void 0 ? void 0 : _b.featureViewManualSave) && !((_c = state.embedInfo) === null || _c === void 0 ? void 0 : _c.viewManualSave)) {
            // There is no uncoordinated view of the entire space station, so there is no need to check the data here
            return actions;
        }
        if (commandName && [commands_1.CollaCommandName.ManualSaveView, commands_1.CollaCommandName.SetViewAutoSave].includes(commandName)) {
            // Manually save the view data. In order to avoid the impact of the field's absence on the view configuration,
            // it is necessary to check the field's existence and filter the abnormal configuration.
            // This filtering scheme is only used to submit data from the client to the server, otherwise this method is not called
            return actions.filter((action) => this._filterFieldExist(action));
        }
        return actions.filter((action) => this._actionFilter(action));
    }
    _filterFieldExist(action) {
        const path = action.p;
        if (!path.includes('views')) {
            return true;
        }
        const state = this._getState();
        const snapshot = store_1.Selectors.getSnapshot(state, this._datasheetId);
        if (!snapshot) {
            return false;
        }
        const fieldMap = snapshot === null || snapshot === void 0 ? void 0 : snapshot.meta.fieldMap;
        const propertyKey = path[3];
        if (action['oi']) {
            return this._fieldIntegrityCheck(propertyKey, action['oi'], fieldMap);
        }
        return true;
    }
    // Reset the current graph configuration, which is consistent with the database data
    static resetViewProperty(state, { datasheetId, viewId, dispatch, onError, shareId }) {
        return __awaiter(this, void 0, void 0, function* () {
            const snapshot = store_1.Selectors.getSnapshot(state, datasheetId);
            const { success, data } = yield ViewPropertyFilter.requestViewData(datasheetId, viewId);
            if (success) {
                const revision = store_1.Selectors.getResourceRevision(state, datasheetId, types_1.ResourceType.Datasheet);
                if (data['revision'] < revision) {
                    // The version of the database is smaller than the local version, it may be that the op is being processed
                    // at the same time as the request, so resend the request
                    return yield this.requestViewData(datasheetId, viewId, shareId);
                }
                if (data['revision'] > revision + 1) {
                    // If the local version is smaller than the database version, you should make up the version number first, and then perform data replacement
                    return this.handleError(onError);
                }
                const viewProperty = data['view'];
                const resetActions = model_1.DatasheetActions.resetView2Action(snapshot, { viewId: viewId, viewProperty: viewProperty });
                if (!resetActions) {
                    return this.handleError(onError);
                }
                dispatch(store_1.StoreActions.applyJOTOperations([{
                        cmd: commands_1.CollaCommandName.SetViewAutoSave,
                        actions: resetActions
                    }], types_1.ResourceType.Datasheet, datasheetId));
                return;
            }
            return this.handleError(onError);
        });
    }
    static handleError(onError) {
        onError === null || onError === void 0 ? void 0 : onError({
            type: types_1.ErrorType.CollaError,
            code: types_1.ErrorCode.EngineCreateFailed,
            message: (0, i18n_1.t)(i18n_1.Strings.manual_save_view_error),
            modalType: types_1.ModalType.Warning
        });
    }
    static requestViewData(datasheetId, viewId, shareId) {
        return __awaiter(this, void 0, void 0, function* () {
            const res = shareId ? yield (0, datasheet_api_1.getShareDstViewDataPack)(datasheetId, viewId, shareId) : yield (0, datasheet_api_1.getDstViewDataPack)(datasheetId, viewId);
            return res.data;
        });
    }
    static getReaderRolePermission(state, datasheetId, permission) {
        var _a, _b, _c;
        const spaceManualSaveViewIsOpen = ((_a = state.labs) === null || _a === void 0 ? void 0 : _a.includes('view_manual_save')) ||
            Boolean((_b = state.share) === null || _b === void 0 ? void 0 : _b.featureViewManualSave) ||
            Boolean((_c = state.embedInfo) === null || _c === void 0 ? void 0 : _c.viewManualSave);
        const viewId = state.pageParams.viewId;
        if (!viewId || !spaceManualSaveViewIsOpen || !permission) {
            return permission;
        }
        const view = (0, selectors_1.getCurrentViewBase)((0, selectors_1.getSnapshot)(state, datasheetId), viewId, datasheetId);
        if (!view || view.autoSave) {
            return permission;
        }
        return Object.assign(Object.assign({}, permission), { viewFilterable: true, columnSortable: true, columnHideable: true, fieldSortable: true, fieldGroupable: true, rowHighEditable: true, columnWidthEditable: true, columnCountEditable: true, viewLayoutEditable: true, viewStyleEditable: true, viewKeyFieldEditable: true, viewColorOptionEditable: true, visualizationEditable: true });
    }
}
exports.ViewPropertyFilter = ViewPropertyFilter;
ViewPropertyFilter.ignoreViewProperty = ['id', 'type', 'rows', 'name', 'lockInfo'];
//# sourceMappingURL=view_property_filter.js.map